import React, { useState, useEffect, useRef } from 'react';
import { X, Minimize2, Maximize2, Send } from 'lucide-react';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { ChatMessage } from '../../types/chat';

interface ChatConversation {
  chatId: string;
  therapistId: string;
  therapistName: string;
  otherMemberId: string;
  otherMemberName: string;
  otherMemberRole: string;
  unreadCount: number;
}

interface Props {
  conversation: ChatConversation;
  currentUserId: string;
  currentUserName: string;
  currentUserRole: string;
  onClose: () => void;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
}

const MiniChat: React.FC<Props> = ({
  conversation,
  currentUserId,
  currentUserName,
  currentUserRole,
  onClose,
  isMinimized = false,
  onToggleMinimize,
}) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleToggleMinimize = () => {
    if (onToggleMinimize) {
      onToggleMinimize();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Marca mensagens como lidas quando abre
  useEffect(() => {
    const markAsRead = async () => {
      try {
        const markChatAsRead = httpsCallable(functions, 'markChatAsRead');
        await markChatAsRead({
          therapistId: conversation.therapistId,
          otherMemberId: conversation.otherMemberId,
        });
      } catch (err) {
        console.error('Erro ao marcar mensagens como lidas:', err);
      }
    };

    if (!isMinimized) {
      markAsRead();
    }
  }, [conversation.therapistId, conversation.otherMemberId, isMinimized]);

  // Escuta mensagens em tempo real
  useEffect(() => {
    const messagesRef = collection(db, 'chats', conversation.chatId, 'messages');
    const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'), limit(50));

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const loadedMessages: ChatMessage[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          senderId: data.senderId,
          senderName: data.senderName,
          senderRole: data.senderRole,
          receiverId: data.receiverId,
          receiverName: data.receiverName,
          receiverRole: data.receiverRole,
          message: data.message,
          timestamp: data.timestamp?.toDate() || new Date(),
          read: data.read,
        };
      }).reverse(); // Inverte para mostrar mais antigas primeiro
      setMessages(loadedMessages);
    });

    return () => unsubscribe();
  }, [conversation.chatId]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;

    setIsSending(true);

    try {
      const sendChatMessage = httpsCallable(functions, 'sendChatMessage');
      
      // Determina quem é o receptor
      const receiverId = currentUserId === conversation.therapistId 
        ? conversation.otherMemberId 
        : conversation.therapistId;
      const receiverName = currentUserId === conversation.therapistId 
        ? conversation.otherMemberName 
        : conversation.therapistName;
      const receiverRole = currentUserId === conversation.therapistId 
        ? conversation.otherMemberRole 
        : 'Terapeuta';

      await sendChatMessage({
        senderId: currentUserId,
        senderName: currentUserName,
        senderRole: currentUserRole,
        receiverId,
        receiverName,
        receiverRole,
        message: message.trim(),
      });

      setMessage('');
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className={`fixed inset-x-0 bottom-0 md:inset-x-auto md:right-4 md:bottom-4 md:w-80 bg-white rounded-t-2xl md:rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden ${
        isMinimized ? '' : 'h-[80dvh] md:h-auto md:max-h-[520px]'
      }`}
    >
      {/* Cabeçalho — alto e com botões grandes para facilitar o toque no mobile */}
      <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <h3 className="font-semibold text-base md:text-sm truncate flex-1">{conversation.otherMemberName}</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={handleToggleMinimize}
            className="p-2 hover:bg-purple-700 active:bg-purple-800 rounded-lg transition-colors"
            title={isMinimized ? 'Maximizar' : 'Minimizar'}
            aria-label={isMinimized ? 'Maximizar conversa' : 'Minimizar conversa'}
          >
            {isMinimized ? <Maximize2 className="w-5 h-5" /> : <Minimize2 className="w-5 h-5" />}
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-purple-700 active:bg-purple-800 rounded-lg transition-colors"
            title="Fechar"
            aria-label="Fechar conversa"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Corpo do chat (escondido quando minimizado) */}
      {!isMinimized && (
        <>
          {/* Mensagens — overscroll-contain evita "arrastar" a página ao chegar no fim */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-3 space-y-3 bg-gray-50 min-h-0">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 text-sm mt-8">
                Nenhuma mensagem ainda. Inicie a conversa!
              </div>
            ) : (
              messages.map((msg) => {
                const isCurrentUser = msg.senderId === currentUserId;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg p-2.5 ${
                        isCurrentUser
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm break-words">{msg.message}</p>
                      <p
                        className={`text-xs mt-1 ${
                          isCurrentUser ? 'text-purple-200' : 'text-gray-500'
                        }`}
                      >
                        {formatMessageTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input de mensagem — padding inferior respeita a área segura do iPhone */}
          <form
            onSubmit={handleSendMessage}
            className="p-3 bg-white border-t border-gray-200 flex-shrink-0"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                /* text-base (16px) no mobile evita o zoom automático do iOS ao focar */
                className="flex-1 px-3 py-2.5 text-base md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isSending}
              />
              <button
                type="submit"
                disabled={!message.trim() || isSending}
                className="p-2.5 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                aria-label="Enviar mensagem"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default MiniChat;
